import { ProgressBar } from '@shared/ui/progress-bar/progress-bar'
import s from './pack.card.module.scss'
import classNames from 'classnames'
import { useProfile } from '@shared/api/useProfile';
import { useNavigate } from 'react-router-dom';
import { TZDate } from '@date-fns/tz';
import { format, differenceInDays, differenceInMinutes } from 'date-fns'
import { ru } from 'date-fns/locale'
import { toast } from 'react-toastify';
import { packages, classes } from '@shared/api/services';
import { queryClient } from '@shared/api/query-client';
import { getNoun } from '@utils/lib/getNoun';
import { canCancelLesson } from '@utils/lib/canCancelLesson';
import { IconCalendar, IconEye, IconEyeOff, IconFileTypePdf } from '@tabler/icons-react'
import { Button } from '@shared/ui/button/button'
import { SupportButton } from '@shared/ui/support-button';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell} from '@shared/ui/table/table';
import { useEffect, useState } from 'react';
import { useMedia } from 'use-media';


export const PackCard = ({ id, role = 'student', teacherImg, status, teacherName, subject, level, lessonsCount, lessonsUsed, price, dateExpire, dateCreated, teacherPriceOneLesson, teacherSlug, onChooseNext, teacherId, studentId, appointments = [], onLeaveReview, onOpenStudent }) => {

    const [ isTableOpen, setIsTableOpen ] = useState(false)
    const [ tableRender, setTableRender ] = useState(false)

    useEffect(() => {
        if (isTableOpen) { setTableRender(true); return }
        const t = setTimeout(() => setTableRender(false), 300)
        return () => clearTimeout(t)
    }, [isTableOpen])

    const isTeacher = role === 'teacher'
    const isMobile = useMedia({ maxWidth: '768px' })

    const navigate = useNavigate()
    const canOpenProfile = isTeacher ? !!studentId : !!teacherSlug
    const openProfile = () => {
        if (!canOpenProfile) return
        if (isTeacher) onOpenStudent?.(studentId)
        else navigate(`/teacher/${teacherSlug}`)
    }

    const economyPack = teacherPriceOneLesson ? teacherPriceOneLesson * lessonsCount - price : null

    const { data: profile } = useProfile()

    const canCancel = (() => {
        if (!dateCreated) return false
        const daysSinceCreated = differenceInDays(new Date(), new TZDate(dateCreated, profile?.timezone))
        return daysSinceCreated <= 2 && lessonsUsed === 0
    })()

    const dateToExpire = dateExpire ? Math.max(0, differenceInDays(new TZDate(dateExpire, profile?.timezone), new Date())) : null
    dateExpire = dateExpire ? format(new TZDate(dateExpire, profile?.timezone), 'dd.MM.yyyy') : '—'
    dateCreated = dateCreated ? format(new TZDate(dateCreated, profile?.timezone), 'dd.MM.yyyy') : '—'

    const nextAppointment = [...appointments]
        .filter((appt) => appt?.status === 'active' && appt?.starts_at)
        .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))[0] ?? null
    const nextLessonLabel = nextAppointment
        ? format(new TZDate(nextAppointment.starts_at, profile?.timezone), 'd MMMM yyyy', { locale: ru })
        : null
    const daysToNextLesson = nextAppointment
        ? Math.max(0, differenceInDays(new TZDate(nextAppointment.starts_at, profile?.timezone), new Date()))
        : null

    const handleCancelPack = async () => {
        if (!canCancel) {
            toast.error('Пакет нельзя отменить: прошло более 2 дней с момента бронирования или уже использованы занятия')
            return
        }
        try {
            await packages.cancelPack(id)
            toast.success('Пакет успешно отменён')
            await queryClient.refetchQueries({ queryKey: ['active-packs'] })
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Ошибка сервера. Попробуйте позже')
        }
    }

    const refetchPacks = async () => {
        await queryClient.refetchQueries({ queryKey: ['active-packs'] })
        await queryClient.refetchQueries({ queryKey: ['archived-packs'] })
    }

    const handleCancelAppointment = async (appointmentId) => {
        try {
            await classes.cancelClass({ id: appointmentId })
            toast.success('Занятие отменено')
            await refetchPacks()
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Ошибка сервера. Попробуйте позже')
        }
    }

    const handleAttachReceipt = async (appointmentId, file) => {
        try {
            await classes.attachAppointmentReceipt({ id: appointmentId, data: file })
            toast.success('Чек загружен')
            await refetchPacks()
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Ошибка сервера. Попробуйте позже')
        }
    }


    const formatAppointment = (appt) => {
        const start = appt?.starts_at ? new TZDate(appt.starts_at, profile?.timezone) : null
        const end = appt?.ends_at ? new TZDate(appt.ends_at, profile?.timezone) : null
        const weekday = start ? format(start, 'EEEE', { locale: ru }) : ''
        return {
            date: start ? format(start, 'd MMMM yyyy', { locale: ru }) : '—',
            weekday: weekday ? weekday.charAt(0).toUpperCase() + weekday.slice(1) : '—',
            time: start ? format(start, 'HH:mm') : '—',
            duration: start && end ? `${differenceInMinutes(end, start)} мин` : '—',
            isActive: appt?.status === 'active',
            // Те же условия, что в AppointmentModal (см. canCancelLesson):
            // ученик — не позднее чем за день до начала или в течение
            // 30 минут после бронирования; преподаватель — всегда
            canCancel: canCancelLesson({
                status: appt?.status,
                startsAt: appt?.starts_at,
                createdAt: appt?.created_at,
                timezone: profile?.timezone,
                isTeacher,
            }),
        }
    }

    return (
        <div className={classNames(s.card, status !== 'active' && s.completed)}>
            <div className={s.main}>
                <div className={s.teacherWrapper}>
                    <img className={classNames(s.teacherImg, canOpenProfile && s.clickable)} onClick={openProfile} alt='Аватарка' src={teacherImg ? teacherImg : process.env.REACT_APP_STORAGE_URL + 'images/default_avatar.png'}/>
                    <div className={s.teacherInfo}>
                        <div className={classNames(s.badge, status === 'active' ? s.active : s.archive)}>{status ==='active' ? 'Активный пакет' : 'Завершён'}</div>
                        <div className={classNames(s.teacherName, canOpenProfile && s.clickable)} onClick={openProfile}>{teacherName}</div>
                        {subject || level ?
                            <div className={s.subText}>
                                {subject ? <span>{subject}</span> : <></>}
                                {subject && level ? <span className={s.dot}>•</span> : <></>}
                                {level ? <span>{level}</span> : <></>}
                            </div>
                            : <></>}
                    </div>
                </div>
                <div className={s.packInfo}>

                    <div className={s.minCard}>
                        <div className={s.packLabel}>Использовано занятий</div>
                        <div className={s.packData}>{lessonsUsed} из {lessonsCount}</div>
                        <ProgressBar currentStep={lessonsUsed} totalSteps={lessonsCount} className={s.progressBar}/>
                    </div>

                    <div className={s.minCard}>
                        <div className={s.packLabel}>Стоимость пакета</div>
                        <div className={s.packData}>{price} ₽</div>
                        {economyPack > 0 ? <div className={s.badge}>Экономия: {economyPack}</div> : <></>}
                    </div>

                    <div className={s.minCard}>
                        <div className={s.packLabel}><IconCalendar size={18}/>Действует до:</div>
                        <div className={s.packData}>{dateExpire}</div>
                        {dateToExpire !== null ? <div className={s.packLabel}>Осталось: {dateToExpire} {getNoun(dateToExpire, 'день', 'дня', 'дней')}</div> : <></>}
                    </div>

                    <div className={s.minCard}>
                        <div className={s.packLabel}><IconCalendar size={18}/>След. занятие</div>
                        <div className={s.packData}>{nextLessonLabel ? nextLessonLabel : 'Не назначено'}</div>
                        {nextLessonLabel
                            ? <div className={s.packLabel}>Осталось: {daysToNextLesson} {getNoun(daysToNextLesson, 'день', 'дня', 'дней')}</div>
                            : null}
                        {!isTeacher && status === 'active' && (
                            <Button className={s.chooseButton} to={`/teacher/booking/${teacherSlug}`} onClick={onChooseNext} label={nextLessonLabel ? 'Записаться ещё' : 'Выбрать'}/>
                        )}
                    </div>
                    
                </div>
            </div>
            {tableRender ?
            <div className={classNames(s.tableAnim, isTableOpen ? s.tableAnimOpen : s.tableAnimClose)}>
            <Table className={classNames(s.table, isTeacher && s.tableTeacher)}>
                <TableHeader className={s.tableHeader}>
                    <TableRow className={s.tableRow}>
                        <TableHead contentClassName={s.headContent} className={s.tableHead}>Дата</TableHead>
                        <TableHead contentClassName={s.headContent} className={s.tableHead}>День недели</TableHead>
                        <TableHead contentClassName={s.headContent} className={s.tableHead}>Время</TableHead>
                        <TableHead contentClassName={s.headContent} className={s.tableHead}>Длительность</TableHead>
                        {isTeacher ? <TableHead contentClassName={s.headContent} className={s.tableHead}>Чек</TableHead> : <></>}
                        <TableHead></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody className={s.tableBody}>
                    {appointments.length > 0 ? appointments.map((appt) => {
                        const row = formatAppointment(appt)
                        return (
                            <TableRow key={appt.id}>
                                <TableCell className={s.tableCell}>{row?.date}</TableCell>
                                <TableCell className={s.tableCell}>{row?.weekday}</TableCell>
                                <TableCell className={s.tableCell}>{row?.time}</TableCell>
                                <TableCell className={s.tableCell}>{row?.duration}</TableCell>
                                {isTeacher ?
                                <TableCell className={s.tableCell}>
                                    {appt?.teacher_receipt?.receipt_file ?
                                        <div className={s.docCell}>
                                            <a className={s.docLink} href={process.env.REACT_APP_STORAGE_URL + appt.teacher_receipt.receipt_file} target='_blank' rel='noreferrer'>
                                                <IconFileTypePdf size={24}/>
                                                <span className={s.docName}>Квитанция.pdf</span>
                                            </a>
                                        </div>
                                        :
                                        <label className={s.uploadReceipt}>
                                            Загрузить чек
                                            <input
                                                type='file'
                                                className={s.receiptInput}
                                                accept='.pdf,image/*'
                                                onChange={(e) => { const file = e.target.files?.[0]; if (file) handleAttachReceipt(appt?.id, file); e.target.value = '' }}
                                            />
                                        </label>
                                    }
                                </TableCell>
                                : <></>}
                                <TableCell className={s.tableCell}>
                                    <div className={s.cellInner}>
                                        <Button
                                            className={classNames(s.buttonCancel, (!row.isActive || !row.canCancel) && s.buttonCancelWeak)}
                                            onClick={row.isActive && row.canCancel ? () => handleCancelAppointment(appt?.id) : undefined}
                                            label='Отменить'
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                        )
                    }) : (
                        <TableRow>
                            <TableCell className={s.tableCell} colSpan={isTeacher ? 6 : 5}>Занятий пока нет</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            </div>
            : <></>
            }
            <div className={s.buttonsGroup}>
                {isMobile ?
                    (isTeacher ?
                        <>
                            <Button className={classNames(s.scheduleButton, s.fullWidthButton)} onClick={() => setIsTableOpen(!isTableOpen)} rightIcon={isTableOpen ? <IconEyeOff size={18}/> : <IconEye size={18}/>} label='Расписание и чеки'/>
                            {status === 'active' ? <Button className={classNames(s.cancelButton, s.fullWidthButton)} onClick={() => handleCancelPack()} label='Отменить пакет'/> : <></>}
                        </>
                        :
                        (status === 'active' ?
                            <>
                                <Button className={s.scheduleButton} onClick={() => setIsTableOpen(!isTableOpen)} rightIcon={isTableOpen ? <IconEyeOff size={18}/> : <IconEye size={18}/>} label='Расписание'/>
                                <Button className={s.cancelButton} onClick={() => handleCancelPack()} label='Отменить запись'/>
                            </>
                            :
                            <>
                                <SupportButton className={s.supportWrapper} buttonClassName={s.profileButton} label='Поддержка'/>
                                <Button className={s.repeatButton} to={`/teacher/booking/${teacherSlug}`} label='Повторить'/>
                            </>
                        )
                    )
                    :
                    <>
                        {status === 'active' ?  <Button className={s.cancelButton} onClick={() => handleCancelPack()} label='Отменить пакет'/> : <></> }
                        <Button className={s.scheduleButton} onClick={() => setIsTableOpen(!isTableOpen)} rightIcon={isTableOpen ? <IconEyeOff size={18}/> : <IconEye size={18}/>} label={isTeacher ? 'Расписание и чеки' : 'Расписание'}/>
                        {!isTeacher && status !== 'active' ?
                        <>
                            <Button className={s.reviewButton} onClick={() => onLeaveReview?.(teacherId)} label='Оставить отзыв'/>
                            <Button className={s.repeatButton} to={`/teacher/booking/${teacherSlug}`} label='Повторить'/>
                        </>
                        : <></>
                        }
                    </>
                }
            </div>
        </div>
    )
}
